/**
 * Preview-access API client. Talks to the Spring Boot backend:
 *   POST   /api/preview-requests               (public)  create a pending request
 *   GET    /api/preview-requests               (team)    list all
 *   POST   /api/preview-requests/{id}/decision (team)    approve subset / reject
 *   DELETE /api/preview-requests/{id}          (team)    delete
 *   POST   /api/preview-auth/login             (public)  prospect sign-in
 *   GET    /api/preview-auth/me                (preview) current approved apps
 *
 * Prospect sessions use a short-lived preview JWT (Bearer). The team console
 * authenticates with a normal admin JWT from /api/auth/login.
 */

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const PREVIEW_TOKEN_KEY = 'pulm_preview_token';
const TEAM_TOKEN_KEY = 'pulm_team_token';

export type RequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface PreviewRequestView {
  id: string;
  name: string;
  email: string;
  company: string | null;
  role: string | null;
  phone: string | null;
  orgSize: string | null;
  industry: string | null;
  emailVerified: boolean;
  useCase: string | null;
  requestedApps: string[];
  approvedApps: string[];
  status: RequestStatus;
  createdAt: string | null;
  decidedAt: string | null;
}

export interface SessionUser {
  name: string;
  email: string;
  approvedApps: string[];
}

/** Thrown when a team API call is unauthorized (missing/expired admin token). */
export class TeamAuthError extends Error {}

function readToken(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function saveToken(key: string, v: string): void {
  try { localStorage.setItem(key, v); } catch { /* storage disabled */ }
}
function dropToken(key: string): void {
  try { localStorage.removeItem(key); } catch { /* noop */ }
}

// ── Public: create ─────────────────────────────────────────────────────────────

/** DEMO email verification — send a code (returned in the response for display). */
export async function sendCode(email: string): Promise<{ ok: boolean; demoCode?: string; message?: string }> {
  try {
    const res = await fetch(`${API_BASE}/preview-auth/send-code`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({} as any));
    return res.ok ? { ok: true, demoCode: data.demoCode } : { ok: false, message: data.message || 'Could not send code.' };
  } catch { return { ok: false, message: 'Cannot reach the server.' }; }
}

export async function verifyCode(email: string, code: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/preview-auth/verify-code`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, code }),
    });
    return res.ok;
  } catch { return false; }
}

export async function submitRequest(input: {
  name: string; email: string; company: string; role: string;
  phone: string; orgSize: string; industry: string;
  useCase: string; requestedApps: string[]; password: string;
}): Promise<void> {
  const res = await fetch(`${API_BASE}/preview-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || 'Could not submit your request. Please try again.');
  }
}

// ── Prospect sign-in ───────────────────────────────────────────────────────────

export type LoginResult =
  | { ok: true; user: SessionUser }
  | { ok: false; code: string; message: string };

export async function login(email: string, password: string): Promise<LoginResult> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/preview-auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    return { ok: false, code: 'NETWORK', message: 'Cannot reach the server.' };
  }
  const data = await res.json().catch(() => ({} as any));
  if (res.ok && data.token) {
    saveToken(PREVIEW_TOKEN_KEY, data.token);
    return { ok: true, user: { name: data.name, email: data.email, approvedApps: data.approvedApps || [] } };
  }
  return { ok: false, code: data.code || 'INVALID', message: data.message || 'Sign in failed.' };
}

export async function currentSession(): Promise<SessionUser | null> {
  const token = readToken(PREVIEW_TOKEN_KEY);
  if (!token) return null;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/preview-auth/me`, { headers: { Authorization: `Bearer ${token}` } });
  } catch {
    return null;
  }
  if (!res.ok) { dropToken(PREVIEW_TOKEN_KEY); return null; }
  return res.json();
}

export function signOut(): void { dropToken(PREVIEW_TOKEN_KEY); }

// ── Pay for services (requires an approved preview session) ─────────────────────

export interface CheckoutItem { id: string; name: string; price: number; }
export interface Receipt { id: string; amount: number; status: string; items: CheckoutItem[]; createdAt: string | null; }

export async function checkout(items: CheckoutItem[], amount: number): Promise<Receipt> {
  const token = readToken(PREVIEW_TOKEN_KEY);
  const res = await fetch(`${API_BASE}/preview-auth/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ items, amount }),
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({} as any));
    throw new Error(b.message || 'Payment could not be completed.');
  }
  return res.json();
}

// ── Team (admin JWT via the normal condo login) ────────────────────────────────

export function teamToken(): string | null { return readToken(TEAM_TOKEN_KEY); }
export function teamLogout(): void { dropToken(TEAM_TOKEN_KEY); }

/** Sign the team in using an existing admin account (reuses /api/auth/login). */
export async function teamLogin(email: string, password: string): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
  } catch { return false; }
  if (!res.ok) return false;
  const data = await res.json().catch(() => ({} as any));
  const token = data.token || (res.headers.get('Authorization') || '').replace(/^Bearer /, '');
  if (!token) return false;
  saveToken(TEAM_TOKEN_KEY, token);
  return true;
}

async function teamFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = teamToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(init.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (res.status === 401 || res.status === 403) { teamLogout(); throw new TeamAuthError('Not authorized'); }
  return res;
}

export async function listRequests(): Promise<PreviewRequestView[]> {
  const res = await teamFetch('/preview-requests');
  if (!res.ok) throw new Error('Failed to load requests.');
  return res.json();
}

export async function decideRequest(id: string, approvedApps: string[]): Promise<PreviewRequestView> {
  const res = await teamFetch(`/preview-requests/${id}/decision`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ approvedApps }),
  });
  if (!res.ok) throw new Error('Failed to update request.');
  return res.json();
}

export async function deleteRequest(id: string): Promise<void> {
  const res = await teamFetch(`/preview-requests/${id}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error('Failed to delete request.');
}
